import { authFetch, getSessionToken } from "site/services/auth-session.service";
import { subscriptionApiBaseUrl } from "site/services/xq-api-base-url";

const OPENAI_SPEECH_URL = "https://api.openai.com/v1/audio/speech";
const EDITOR_TTS_MODEL = "gpt-4o-mini-tts";

export type EditorTtsVoice = { id: string; title: string };

export const EDITOR_TTS_VOICES: readonly EditorTtsVoice[] = [
  { id: "ash", title: "Ash" },
  { id: "alloy", title: "Alloy" },
];

const VOICE_TTS_INSTRUCTIONS: Record<string, string> = {
  ash: [
    "Voice: Soft, gentle, and velvety — warm baritone timbre, never harsh or gruff, and not feminine or breathy.",
    "Tone: Tender and reassuring, like a calm teacher; understated, never theatrical or overly bright.",
    "Arabic pronunciation: Extremely accurate; articulate every letter and vowel clearly without swallowing sounds.",
    "Delivery: Smooth, unhurried, and even; light emotion only; easy to follow for learners.",
    "Style: Natural human speech with precise Arabic diction and clean pauses; no character acting.",
  ].join("\n\n"),
  alloy: [
    "Voice: Gentle, smooth, and velvety — rounded and soft, not rough or booming, and not feminine.",
    "Tone: Kind and composed, quietly confident; avoid sharp edges, rasp, or macho bravado.",
    "Arabic pronunciation: Strictly correct and crisp; pronounce each Arabic letter clearly and avoid slurring.",
    "Delivery: Fluid, relaxed rhythm; calm warmth; moderately expressive but never loud or forceful.",
    "Style: Polished educational narration; prioritize clarity, correctness, and silky phrasing.",
  ].join("\n\n"),
};

export const DEFAULT_EDITOR_TTS_VOICE = EDITOR_TTS_VOICES[0].id;

function voiceInstructions(voice: string): string | undefined {
  return VOICE_TTS_INSTRUCTIONS[voice];
}

export type EditorTtsConfig = {
  api_key?: string;
};

let ttsApiKeyCache: string | null = null;

function authHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const token = getSessionToken();
  const h: Record<string, string> = { ...extra };
  if (token) {
    h.Authorization = `Bearer ${token}`;
  }
  return h;
}

function apiBase(): string {
  const base = subscriptionApiBaseUrl();
  if (!base) {
    throw new Error("VITE_XQ_API_BASE_URL is not set");
  }
  return base;
}

export async function fetchEditorTtsConfig(): Promise<EditorTtsConfig> {
  const r = await authFetch(`${apiBase()}/editor/tts/config`, {
    method: "GET",
    headers: authHeaders(),
  });
  if (!r.ok) {
    throw new Error(`editor tts config: HTTP ${r.status}`);
  }
  const cfg = (await r.json()) as EditorTtsConfig;
  const key = cfg.api_key?.trim() ?? "";
  ttsApiKeyCache = key || null;
  return cfg;
}

async function getEditorOpenAiApiKey(): Promise<string> {
  if (ttsApiKeyCache) {
    return ttsApiKeyCache;
  }
  const cfg = await fetchEditorTtsConfig();
  const key = cfg.api_key?.trim() ?? "";
  if (!key) {
    throw new Error("OpenAI API key not available for editor");
  }
  return key;
}

export { getEditorOpenAiApiKey };

export async function synthesizeEditorSpeech(text: string, voice: string): Promise<Blob> {
  const apiKey = await getEditorOpenAiApiKey();
  const voiceId = voice || DEFAULT_EDITOR_TTS_VOICE;
  const instructions = voiceInstructions(voiceId);
  const payload: Record<string, string> = {
    model: EDITOR_TTS_MODEL,
    voice: voiceId,
    input: text,
    response_format: "aac",
  };
  if (instructions) {
    payload.instructions = instructions;
  }

  const r = await fetch(OPENAI_SPEECH_URL, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${apiKey}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!r.ok) {
    const detail = await r.text().catch(() => "");
    throw new Error(`OpenAI speech: HTTP ${r.status}${detail ? ` — ${detail}` : ""}`);
  }
  const raw = await r.blob();
  if (raw.type && raw.type !== "application/octet-stream") {
    return raw;
  }
  return new Blob([raw], { type: "audio/aac" });
}

export async function uploadEditorSound(
  textId: string,
  rowId: number,
  blob: Blob,
  filename = "voice.aac",
): Promise<string> {
  const form = new FormData();
  form.append("file", blob, filename.endsWith(".aac") ? filename : `${filename}.aac`);
  const r = await authFetch(
    `${apiBase()}/editor/texts/${encodeURIComponent(textId)}/sounds/${encodeURIComponent(String(rowId))}`,
    {
      method: "POST",
      headers: authHeaders(),
      body: form,
    },
  );
  if (!r.ok) {
    throw new Error(`editor upload sound: HTTP ${r.status}`);
  }
  const json = (await r.json()) as { path?: string };
  return json.path?.trim() ?? "";
}
